import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import { errorFields, errorMessage } from '../lib/api-error';
import { toast } from '../lib/toast';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../components/ui';
import { SECRET_MASK, type SettingDefinition, type SettingsResponse } from './settings-types';

/** Kullanıcının dokunduğu anahtarlar — yalnızca bunlar PUT edilir. */
type Draft = Record<string, string>;

export function SettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const readOnly = isReadOnly(user);

  const [draft, setDraft] = useState<Draft>({});
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [invalid, setInvalid] = useState<Set<string>>(new Set());

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsResponse>('/settings'),
  });

  const save = useMutation({
    mutationFn: (patch: Draft) => api.put<{ changed: string[] }>('/settings', patch),
    onSuccess: (res) => {
      setDraft({});
      setRevealed(new Set());
      setInvalid(new Set());
      /*
       * Sunucu değerleri NORMALİZE eder (KDV numarasına 'BE' ekler, IBAN'ı
       * büyük harfe çevirir, işletme numarasından noktaları atar) ama PUT
       * yanıtı yalnızca değişen anahtar adlarını döner. Normalize edilmiş
       * hâli görmek için yeniden okumak ŞART.
       */
      void qc.invalidateQueries({ queryKey: ['settings'] });
      toast.success(
        res.changed.length === 0 ? 'Değişiklik yok.' : `${res.changed.length} ayar kaydedildi.`,
      );
    },
    onError: (err) => {
      setInvalid(errorFields(err));
      toast.error(err);
    },
  });

  const groups = useMemo(() => {
    if (!settings.data) return [];
    const { definitions, groups: order } = settings.data;
    const byGroup = new Map<string, Array<[string, SettingDefinition]>>();
    for (const [key, def] of Object.entries(definitions)) {
      const list = byGroup.get(def.group) ?? [];
      list.push([key, def]);
      byGroup.set(def.group, list);
    }
    for (const list of byGroup.values()) {
      list.sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0));
    }
    // Bölüm sırası SUNUCUDAN gelir; panel kendi kopyasını tutmaz.
    return order
      .filter((g) => byGroup.has(g))
      .map((g) => ({ title: g, fields: byGroup.get(g) ?? [] }));
  }, [settings.data]);

  if (settings.isPending) {
    return (
      <div className="py-20 text-center">
        <Spinner size={22} />
      </div>
    );
  }

  if (settings.isError) {
    return (
      <>
        <PageHeader title="Ayarlar" />
        <Card>
          <p className="px-4 py-10 text-center text-sm text-[var(--c-danger)]">
            {errorMessage(settings.error)}
          </p>
        </Card>
      </>
    );
  }

  const values = settings.data.values;
  const dirtyCount = Object.keys(draft).length;

  function setField(key: string, value: string) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function currentValue(key: string, def: SettingDefinition): string {
    if (key in draft) return draft[key] ?? '';
    const stored = values[key];
    if (typeof stored === 'string') return stored;
    return def.default;
  }

  function submit() {
    if (dirtyCount === 0) return;
    /*
     * ⚠ YALNIZCA kirli alanlar gönderilir. Tüm haritayı göndermek burada
     * çalışmaz: sunucu bilinmeyen anahtarda UNKNOWN_SETTING_KEY, yazma yetkisi
     * olmayan anahtarda ADMIN_ONLY_SETTING fırlatır ve TEK istisna TÜM kaydı
     * iptal eder. Ayrıca maskeli sır değerini geri göndermek gereksiz yere
     * "değişmedi" turu attırır.
     */
    save.mutate(draft);
  }

  return (
    <>
      <PageHeader
        title="Ayarlar"
        description="Şirket bilgileri .env'de değil veritabanındadır ve buradan değiştirilir."
        actions={
          !readOnly && (
            <Button loading={save.isPending} disabled={dirtyCount === 0} onClick={submit}>
              {dirtyCount === 0 ? 'Kaydet' : `Kaydet (${dirtyCount})`}
            </Button>
          )
        }
      />

      {readOnly && (
        <Card className="mb-4">
          <p className="px-4 py-3 text-sm text-[var(--c-warn)]">
            Salt okuma yetkisiyle görüntülüyorsunuz; değişiklik kaydedilemez.
          </p>
        </Card>
      )}

      <div className="space-y-4">
        {groups.map((group) => (
          <Card key={group.title}>
            <CardHeader title={group.title} />
            <div className="space-y-4 px-4 py-4">
              {group.fields.map(([key, def]) => (
                <SettingField
                  key={key}
                  settingKey={key}
                  def={def}
                  value={currentValue(key, def)}
                  isSet={values[`${key}_isSet`] === true}
                  dirty={key in draft}
                  revealed={revealed.has(key)}
                  invalid={invalid.has(key)}
                  disabled={readOnly}
                  onReveal={() => {
                    setRevealed((s) => new Set(s).add(key));
                    // Sır alanı düzenlemeye açılırken BOŞ başlar; maske asla
                    // geri gönderilmez.
                    setField(key, '');
                  }}
                  onCancelReveal={() => {
                    setRevealed((s) => {
                      const n = new Set(s);
                      n.delete(key);
                      return n;
                    });
                    setDraft((d) => {
                      const n = { ...d };
                      delete n[key];
                      return n;
                    });
                  }}
                  onChange={(v) => setField(key, v)}
                />
              ))}
            </div>
          </Card>
        ))}
      </div>

      <p className="mt-6 text-xs text-[var(--c-ink-faint)]">
        Denetim kaydına yalnızca değişen <strong>anahtar adları</strong> yazılır; değerler yazılmaz.
      </p>
    </>
  );
}

// ─────────────────────────────────────────────────────────────── alan

interface FieldProps {
  settingKey: string;
  def: SettingDefinition;
  value: string;
  isSet: boolean;
  dirty: boolean;
  revealed: boolean;
  invalid: boolean;
  disabled: boolean;
  onReveal: () => void;
  onCancelReveal: () => void;
  onChange: (value: string) => void;
}

function SettingField(props: FieldProps) {
  const { settingKey, def, value, isSet, dirty, revealed, invalid, disabled } = props;
  const error = invalid ? 'Bu değer sunucu tarafından reddedildi.' : null;
  const label = dirty ? `${def.label} •` : def.label;

  // ── Sır-sınıfı alan: DEĞER değil, DURUM gösterilir.
  if (def.secret && !revealed) {
    return (
      <div>
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[13px] font-medium">{def.label}</span>
          <Badge tone={isSet ? 'ok' : 'neutral'}>{isSet ? 'tanımlı' : 'tanımsız'}</Badge>
        </div>
        {def.hint && <p className="mb-1 text-xs text-[var(--c-ink-faint)]">{def.hint}</p>}
        <div className="flex items-center gap-2">
          <code className="rounded-[var(--r-sm)] bg-[var(--c-surface-alt)] px-2 py-1 text-xs">
            {isSet ? SECRET_MASK : '—'}
          </code>
          {!disabled && (
            <Button variant="secondary" size="sm" onClick={props.onReveal}>
              {isSet ? 'Değiştir' : 'Tanımla'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (def.secret && revealed) {
    return (
      <div>
        <Input
          label={`${def.label} (yeni değer)`}
          hint={def.hint}
          error={error}
          type="password"
          autoComplete="off"
          value={value}
          disabled={disabled}
          onChange={(e) => props.onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={props.onCancelReveal}
          className="mt-1 text-xs text-[var(--c-ink-soft)] hover:underline"
        >
          Vazgeç (mevcut değer korunur)
        </button>
      </div>
    );
  }

  switch (def.type) {
    case 'boolean':
      return (
        <Checkbox
          label={label}
          hint={def.hint}
          checked={value === 'true'}
          disabled={disabled}
          onChange={(e) => props.onChange(e.target.checked ? 'true' : 'false')}
        />
      );

    case 'enum':
      return (
        <Select
          label={label}
          hint={def.hint}
          error={error}
          value={value}
          disabled={disabled}
          onChange={(e) => props.onChange(e.target.value)}
        >
          {(def.values ?? []).map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </Select>
      );

    case 'number':
      return (
        <Input
          label={label}
          hint={def.hint}
          error={error}
          type="number"
          step="any"
          value={value}
          disabled={disabled}
          onChange={(e) => props.onChange(e.target.value)}
        />
      );

    case 'text':
    case 'json':
      return (
        <Textarea
          label={label}
          hint={def.type === 'json' ? (def.hint ?? 'JSON biçiminde') : def.hint}
          error={error}
          rows={def.type === 'json' ? 3 : 4}
          value={value}
          disabled={disabled}
          onChange={(e) => props.onChange(e.target.value)}
          className={def.type === 'json' ? 'font-mono text-xs' : undefined}
        />
      );

    default:
      return (
        <Input
          label={label}
          hint={def.hint}
          error={error}
          value={value}
          disabled={disabled}
          onChange={(e) => props.onChange(e.target.value)}
          // Tarih alanları sunucuda YYYY-AA-GG bekliyor; tarayıcı seçici bunu
          // birebir bu biçimde üretir.
          type={settingKey.endsWith('_date') || settingKey.endsWith('_from') ? 'date' : 'text'}
        />
      );
  }
}
